import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import SearchIcon from '@heroicons/react/solid/SearchIcon';
import { useRouteMatch } from 'react-router';
import { SearchEntityType } from '@tribeplatform/gql-client/types';
import { useRouter } from '@tribeplatform/react-sdk';
import { useSearch } from '@tribeplatform/react-sdk/hooks';
import { getSearchKey } from '@tribeplatform/react-sdk/utils/keys';
import { Autocomplete } from '@tribeplatform/react-ui-kit/Autocomplete';
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons';
import { useDebounce } from '../common/hooks/useDebounce';
import { T, useI18n } from '../i18n';
import { cleanSearchQuery } from '../utils/search';
import { SearchAutocompleteItems } from './SearchAutocompleteItems';
import { SearchRowPage } from './SearchRow';
import { getSearchItemLink, getSearchPageLink } from './utils';
const PAGE_RESULT = 'search-page-shortcut';
export const SearchBar = ({ autoFocus = false, query: inputQuery = '', type = null, entityType = null, placeholder: maybePlaceholder, }) => {
    const { $t } = useI18n();
    const [dirtyQuery, setDirtyQuery] = useState('');
    const query = cleanSearchQuery(dirtyQuery);
    const { push, replace } = useRouter();
    const debouncedQuery = useDebounce(query, 300);
    const match = useRouteMatch('/search');
    const enabled = match === null;
    useEffect(() => {
        setDirtyQuery(inputQuery || '');
    }, [inputQuery, type]);
    const { data, isLoading, isFetching } = useSearch({
        variables: {
            input: {
                query: debouncedQuery
                    ? debouncedQuery + (entityType ? ` +for:${entityType}` : '')
                    : '',
                includeExternal: true,
            },
        },
        fields: 'all',
        useQueryOptions: {
            queryKey: getSearchKey({
                variables: {
                    input: { query: debouncedQuery },
                },
            }),
            keepPreviousData: true,
            enabled,
        },
    });
    const onSelect = (value) => {
        if (value && value.entityType !== SearchEntityType.External) {
            if (value === PAGE_RESULT) {
                push(getSearchPageLink({ search: dirtyQuery, type }));
            }
            else {
                push(getSearchItemLink(value));
            }
        }
    };
    const onEnter = ({ highlightedIndex, inputValue }) => {
        if (inputValue && highlightedIndex < 1) {
            push(getSearchPageLink({ search: dirtyQuery, type }));
        }
    };
    const placeholder = maybePlaceholder !== null && maybePlaceholder !== void 0 ? maybePlaceholder : $t({
        id: 'Generics.SearchDotDotDot',
        defaultMessage: 'Search...',
    });
    return (_jsx("div", { className: "flex items-center flex-1", children: _jsxs(Autocomplete, { itemToString: value => { var _a; return ((_a = value) === null || _a === void 0 ? void 0 : _a.title) || ''; }, value: dirtyQuery, onChange: onSelect, options: [
                PAGE_RESULT,
                ...((data === null || data === void 0 ? void 0 : data.hits.flatMap(category => category.hits)) || []),
            ], onInputChange: query => {
                setDirtyQuery(query);
                if (!enabled) {
                    replace(getSearchPageLink({ search: query, type }));
                }
            }, className: "w-full relative", loading: isLoading, defaultHighlightedIndex: enabled ? 0 : undefined, clearInputOnSelect: enabled, clearInputOnBlur: enabled, children: [_jsx(Autocomplete.Input, { autoFocus: autoFocus, leadingIcon: enabled && isFetching ? (_jsx(SpinnerIcon, { className: "animate-spin" }, void 0)) : (_jsx(SearchIcon, {}, void 0)), placeholder: placeholder, onEnter: onEnter, showClearButton: false, inputMode: "search" }, void 0), enabled && (_jsxs(Autocomplete.Items, { className: "h-full overflow-y-auto isolate", children: [_jsx(Autocomplete.Item, { index: 0, value: "search-page-shortcut-value", children: _jsx(SearchRowPage, { search: dirtyQuery, link: getSearchPageLink({ search: dirtyQuery, type }), spacing: "sm" }, void 0) }, void 0), _jsx(SearchAutocompleteItems, { data: data }, void 0), isLoading && (_jsx(Autocomplete.ItemsEmpty, { children: _jsx(T, { id: "Generics.SearchingDotDotDto", defaultMessage: "Searching..." }, void 0) }, void 0))] }, void 0))] }, void 0) }, void 0));
};
//# sourceMappingURL=SearchBar.js.map